import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { profileKeys, updateProfile, type Profile } from '@entities/Profile';
import {
  clearPendingLanguage,
  decideInterfaceLanguage,
  readPendingLanguage,
  uiLang,
  type UiLang,
} from '../lib/interfaceLanguage';
import { useAuthStore } from './useAuthStore';

// Одна запись в профиль на вкладку: AppGate монтируется заново при переходе между оболочкой и
// полноэкранными экранами, а запрос мог ещё не вернуться.
let pushInFlight = false;

/**
 * Язык интерфейса при загрузке профиля (B4, 2026-10-20): язык профиля — или выбранный при
 * регистрации в этом браузере, если профиль о нём ещё не знает; тогда профиль догоняет через
 * PUT /profile, а интерфейс не переключается. Возвращает язык, который показан.
 */
export function useInterfaceLanguageSync() {
  const { i18n } = useTranslation();
  const qc = useQueryClient();

  return useCallback(
    (profile: Profile): UiLang => {
      const decision = decideInterfaceLanguage({
        profile: profile.language_interface,
        pending: readPendingLanguage(useAuthStore.getState().user?.id),
        current: uiLang(i18n.resolvedLanguage || i18n.language),
      });
      if (decision.apply) void i18n.changeLanguage(decision.apply);
      if (decision.clearPending) clearPendingLanguage();
      if (decision.push && !pushInFlight) {
        pushInFlight = true;
        updateProfile({ language_interface: decision.push })
          .then((fresh) => {
            clearPendingLanguage();
            qc.setQueryData(profileKeys.current, fresh);
          })
          .catch(() => {
            // Не записалось — интерфейс всё равно на выбранном языке; повторим при следующей загрузке профиля.
          })
          .finally(() => {
            pushInFlight = false;
          });
      }
      return decision.shown;
    },
    [i18n, qc],
  );
}
